import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { ImageUploaderComponent } from '../../components/image-uploader/image-uploader.component';
import { CategoryService } from '../../core/services/category.service';
import { CollectionService } from '../../core/services/collection.service';
import { SubCategoryService } from '../../core/services/sub-category.service';
import { Category } from '../../models/category.interface';
import { Collection } from '../../models/collection.interface';
import { SubCategory } from '../../models/sub-category.interface';

@Component({
  selector: 'app-category-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SidebarComponent, NavbarComponent, ImageUploaderComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      [pageTitle]="activeTab === 'subcategories' ? 'Sub-Categories' : (activeTab === 'collections' ? 'Collections' : 'Categories')"
      pageSubtitle="Taxonomy Flow: 1. Category ➔ 2. Sub-Category ➔ 3. Collection ➔ Products"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- TABS BAR -->
        <div class="tabs-bar glass-card">
          <button
            class="tab-btn"
            [class.active]="activeTab === 'categories'"
            (click)="setTab('categories')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/>
            </svg>
            1. Categories
            <span class="tab-count">{{ categories.length }}</span>
          </button>

          <button
            class="tab-btn"
            [class.active]="activeTab === 'subcategories'"
            (click)="setTab('subcategories')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 6h16M4 12h10M4 18h7"/>
              <circle cx="18" cy="15" r="3"/>
            </svg>
            2. Sub-Categories
            <span class="tab-count">{{ subCategories.length }}</span>
          </button>

          <button
            class="tab-btn"
            [class.active]="activeTab === 'collections'"
            (click)="setTab('collections')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            3. Collections
            <span class="tab-count">{{ collections.length }}</span>
          </button>
        </div>

        <!-- ========================================== -->
        <!-- TAB 1: CATEGORIES TABLE & FORM             -->
        <!-- ========================================== -->
        <div *ngIf="activeTab === 'categories'" class="category-layout animate-in">

          <!-- ADD / EDIT CATEGORY FORM -->
          <section class="glass-card add-card">
            <div class="form-card-header">
              <div class="icon-box icon-box-gold icon-box-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/>
                </svg>
              </div>
              <div>
                <h3 class="font-serif title-gold" style="font-size: 16px; margin:0;">
                  {{ editingCatId ? 'Edit Category' : 'New Category' }}
                </h3>
                <p class="sub-text" style="margin:2px 0 0 0;">Top-level product line (Mobile, Laptop Sleeve, etc.)</p>
              </div>
            </div>

            <form (ngSubmit)="onSaveCategory()" class="add-cat-form">
              <div class="form-group">
                <label class="form-label">Category Name *</label>
                <input type="text" class="form-control" [(ngModel)]="currentCat.name" name="catName" placeholder="e.g. Mobile Cases, Laptop Sleeves" required />
              </div>

              <div class="form-group">
                <label class="form-label">Icon Emoji</label>
                <input type="text" class="form-control" [(ngModel)]="currentCat.icon" name="catIcon" placeholder="📱, 💻, 🎧, ⌚, ✨" />
              </div>

              <div class="form-group mobile-toggle-card">
                <label class="checkbox-label" style="font-weight:600; color:var(--text-primary);">
                  <input type="checkbox" [(ngModel)]="currentCat.isMobile" name="catIsMobile" />
                  <span>📱 Mobile Devices Line</span>
                </label>
                <p class="sub-text" style="font-size:11px; margin:4px 0 0 24px;">
                  Links products to phone models, device groups, and case materials.
                </p>
              </div>

              <div class="form-group mobile-toggle-card">
                <label class="checkbox-label" style="font-weight:600; color:var(--text-primary);">
                  <input type="checkbox" [(ngModel)]="currentCat.showInShopMenu" name="catShowInShopMenu" />
                  <span>🛍️ Show in Store Shop Menu</span>
                </label>
                <p class="sub-text" style="font-size:11px; margin:4px 0 0 24px;">
                  Controls whether this category appears in the customer store shop menu.
                </p>
              </div>

              <div class="form-group">
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="2" [(ngModel)]="currentCat.description" name="catDesc" placeholder="Summary of this product category..."></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Cover Image</label>
                <app-image-uploader
                  [currentImageUrl]="currentCat.imageUrl || ''"
                  [cloudinaryId]="currentCat.cloudinaryId || ''"
                  (imageUploaded)="onCatImageUploaded($event)"
                  (imageRemoved)="onCatImageRemoved()"
                ></app-image-uploader>
              </div>

              <div class="form-actions">
                <button *ngIf="editingCatId" type="button" (click)="resetCatForm()" class="btn-secondary">Cancel</button>
                <button type="submit" class="btn-gold" [disabled]="savingCat">
                  <span class="spinner" *ngIf="savingCat"></span>
                  <svg *ngIf="!savingCat" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17 21 17 13 7 13 7 21"/>
                  </svg>
                  {{ savingCat ? 'Saving…' : (editingCatId ? 'Update Category' : 'Create Category') }}
                </button>
              </div>
            </form>
          </section>

          <!-- CATEGORIES TABLE -->
          <section class="glass-card list-card">
            <div class="list-header">
              <div>
                <h3 class="font-serif title-gold" style="font-size: 17px; margin:0;">Active Categories</h3>
                <p class="sub-text" style="margin:3px 0 0 0;">Top-level product lines that group Collections</p>
              </div>
              <span class="count-pill">{{ categories.length }} Categories</span>
            </div>

            <div class="table-wrap">
              <table class="custom-table">
                <thead>
                  <tr>
                    <th style="width: 44px;">#</th>
                    <th>Category</th>
                    <th>Slug</th>
                    <th>Type</th>
                    <th>Shop Menu</th>
                    <th>Linked Sub-Categories</th>
                    <th style="text-align: right;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let cat of getFilteredCategories(); let i = index">
                    <td class="sub-text">{{ i + 1 }}</td>
                    <td>
                      <div style="display:flex; align-items:center; gap:10px;">
                        <span style="font-size:22px;">{{ cat.icon || '📁' }}</span>
                        <div>
                          <strong style="color:var(--text-primary); font-size:14px;">{{ cat.name }}</strong>
                          <div class="sub-text" style="font-size:11.5px; max-width:260px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
                            {{ cat.description || 'Primary product category' }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <code style="font-size:12px; color:var(--text-muted);">{{ cat.slug }}</code>
                    </td>
                    <td>
                      <span class="badge" [class.badge-gold]="cat.isMobile !== false" [class.badge-secondary]="cat.isMobile === false">
                        {{ cat.isMobile !== false ? '📱 Mobile' : '💻 Non-Mobile' }}
                      </span>
                    </td>
                    <td>
                      <button 
                        type="button"
                        class="badge" 
                        [class.badge-gold]="cat.showInShopMenu !== false" 
                        [class.badge-secondary]="cat.showInShopMenu === false"
                        (click)="toggleShopMenuVisibility(cat)"
                        style="cursor:pointer; border:1px solid currentColor;"
                        title="Toggle visibility in store shop menu"
                      >
                        {{ cat.showInShopMenu !== false ? '✓ Visible' : '✗ Hidden' }}
                      </button>
                    </td>
                    <td>
                      <button class="nav-chip-btn" (click)="openSubCategoriesForCategory(cat.slug)">
                        🏷️ {{ getSubCategoryCountForCategory(cat.slug) }} Sub-Categories →
                      </button>
                    </td>
                    <td style="text-align: right;">
                      <div class="action-btn-group">
                        <button (click)="editCategory(cat)" class="btn-secondary btn-icon" title="Edit Category">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button (click)="deleteCategory(cat)" class="btn-danger btn-icon" title="Delete Category">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr *ngIf="categories.length === 0">
                    <td colspan="6" class="text-center" style="padding: 36px;">
                      <div style="font-size:26px; margin-bottom:6px;">📁</div>
                      <div style="color:var(--text-secondary);">No categories found. Create one using the form on the left!</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <!-- ========================================== -->
        <!-- TAB 2: SUB-CATEGORIES TABLE & FORM         -->
        <!-- ========================================== -->
        <div *ngIf="activeTab === 'subcategories'" class="category-layout animate-in">

          <!-- ADD / EDIT SUB-CATEGORY FORM -->
          <section class="glass-card add-card">
            <div class="form-card-header">
              <div class="icon-box icon-box-gold icon-box-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 6h16M4 12h10M4 18h7"/>
                  <circle cx="18" cy="15" r="3"/>
                </svg>
              </div>
              <div>
                <h3 class="font-serif title-gold" style="font-size: 16px; margin:0;">
                  {{ editingSubCatId ? 'Edit Sub-Category' : 'New Sub-Category' }}
                </h3>
                <p class="sub-text" style="margin:2px 0 0 0;">Sub-category under a parent Category (e.g. iPhone Cases)</p>
              </div>
            </div>

            <form (ngSubmit)="onSaveSubCategory()" class="add-cat-form">
              <!-- PARENT CATEGORY SELECT -->
              <div class="form-group">
                <label class="form-label">Parent Category *</label>
                <select class="form-control" [(ngModel)]="currentSubCat.category" name="subCatParent" required (ngModelChange)="onSubCategoryParentChange($event)">
                  <option value="" disabled>Select Parent Category</option>
                  <option *ngFor="let cat of categories" [value]="cat.slug">
                    {{ cat.icon }} {{ cat.name }}
                  </option>
                </select>
                <p class="sub-text" style="font-size:11px; margin:4px 0 0 0;">
                  Step 2 in flow: Category ➔ Sub-Category ➔ Collections.
                </p>
              </div>

              <div class="form-group">
                <label class="form-label">Sub-Category Name *</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="currentSubCat.name"
                  name="subCatName"
                  placeholder="e.g. iPhone Cases, Samsung Cases, Automotive"
                  required
                />
              </div>

              <div class="form-group">
                <label class="form-label">Icon / Emoji</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="currentSubCat.icon"
                  name="subCatIcon"
                  placeholder="🏷️, 📱, 🏎️, 🛡️, 💎"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Description</label>
                <textarea
                  class="form-control"
                  rows="2"
                  [(ngModel)]="currentSubCat.description"
                  name="subCatDesc"
                  placeholder="Subcategory description..."
                ></textarea>
              </div>

              <div class="form-group">
                <label class="checkbox-label" style="font-weight:600; color:var(--text-primary);">
                  <input type="checkbox" [(ngModel)]="currentSubCat.active" name="subCatActive" />
                  <span>Active & Visible in Catalog</span>
                </label>
              </div>

              <div class="form-group">
                <label class="form-label">Cover Image (Optional)</label>
                <app-image-uploader
                  [currentImageUrl]="currentSubCat.imageUrl || ''"
                  [cloudinaryId]="currentSubCat.cloudinaryId || ''"
                  (imageUploaded)="onSubCatImageUploaded($event)"
                  (imageRemoved)="onSubCatImageRemoved()"
                ></app-image-uploader>
              </div>

              <div class="form-actions">
                <button *ngIf="editingSubCatId" type="button" (click)="resetSubCatForm()" class="btn-secondary">Cancel</button>
                <button type="submit" class="btn-gold" [disabled]="savingSubCat">
                  <span class="spinner" *ngIf="savingSubCat"></span>
                  <svg *ngIf="!savingSubCat" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17 21 17 13 7 13 7 21"/>
                  </svg>
                  {{ savingSubCat ? 'Saving…' : (editingSubCatId ? 'Update Sub-Category' : 'Create Sub-Category') }}
                </button>
              </div>
            </form>
          </section>

          <!-- SUB-CATEGORIES TABLE -->
          <section class="glass-card list-card">
            <!-- Filter & Search Toolbar -->
            <div class="subcat-toolbar">
              <div class="filter-group">
                <label class="form-label" style="margin:0; font-size:12px;">Parent Category:</label>
                <select class="form-control" style="width: auto; min-width: 180px;" [(ngModel)]="filterSubCategoryCategory">
                  <option value="all">All Categories ({{ subCategories.length }})</option>
                  <option *ngFor="let cat of categories" [value]="cat.slug">
                    {{ cat.icon }} {{ cat.name }} ({{ getSubCategoryCountForCategory(cat.slug) }})
                  </option>
                </select>
              </div>

              <div class="search-wrap">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  class="form-control search-input"
                  [(ngModel)]="searchSubCatQuery"
                  placeholder="Search sub-categories..."
                />
              </div>
            </div>

            <!-- Subcategories Table -->
            <div class="table-wrap">
              <table class="custom-table">
                <thead>
                  <tr>
                    <th style="width: 44px;">#</th>
                    <th>Sub-Category</th>
                    <th>Parent Category</th>
                    <th>Linked Collections</th>
                    <th>Status</th>
                    <th style="text-align: right;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let sub of getFilteredSubCategories(); let i = index">
                    <td class="sub-text">{{ i + 1 }}</td>
                    <td>
                      <div style="display:flex; align-items:center; gap:10px;">
                        <span style="font-size:18px;">{{ sub.icon || '🏷️' }}</span>
                        <div>
                          <strong style="color:var(--text-primary); font-size:13px;">{{ sub.name }}</strong>
                          <div class="sub-text" style="font-size:11px;">{{ sub.slug }}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="badge badge-gold">{{ getCategoryDisplayName(sub.category) }}</span>
                    </td>
                    <td>
                      <button class="nav-chip-btn" (click)="openCollectionsForSubCategory(sub.slug || '')">
                        ✨ {{ getCollectionCountForSubCategory(sub.slug || '') }} Collections →
                      </button>
                    </td>
                    <td>
                      <span class="status-pill" [class.status-active]="sub.active !== false" [class.status-inactive]="sub.active === false">
                        {{ sub.active !== false ? '● Active' : '○ Inactive' }}
                      </span>
                    </td>
                    <td style="text-align: right;">
                      <div class="action-btn-group">
                        <button (click)="editSubCategory(sub)" class="btn-secondary btn-icon" title="Edit Sub-Category">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button (click)="deleteSubCategory(sub)" class="btn-danger btn-icon" title="Delete Sub-Category">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr *ngIf="getFilteredSubCategories().length === 0">
                    <td colspan="6" class="text-center" style="padding: 32px;">
                      <div style="font-size:24px; margin-bottom:6px;">🏷️</div>
                      <div style="color:var(--text-secondary);">No sub-categories found. Create one using the form on the left!</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <!-- ========================================== -->
        <!-- TAB 3: COLLECTIONS TABLE & FORM            -->
        <!-- ========================================== -->
        <div *ngIf="activeTab === 'collections'" class="category-layout animate-in">

          <!-- ADD / EDIT COLLECTION FORM -->
          <section class="glass-card add-card">
            <div class="form-card-header">
              <div class="icon-box icon-box-gold icon-box-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>
              <div>
                <h3 class="font-serif title-gold" style="font-size: 16px; margin:0;">
                  {{ editingCollectionId ? 'Edit Collection' : 'New Collection' }}
                </h3>
                <p class="sub-text" style="margin:2px 0 0 0;">Step 3 in flow: Category ➔ Sub-Category ➔ Collection</p>
              </div>
            </div>

            <form (ngSubmit)="onSaveCollection()" class="add-cat-form">
              <!-- PARENT CATEGORIES MULTI-SELECT -->
              <div class="form-group">
                <label class="form-label" style="display:flex; justify-content:space-between; align-items:center;">
                  <span>Parent Categories * (Multi-Select)</span>
                  <span class="sub-text" style="font-size:11px; text-transform:none;">Select all that apply</span>
                </label>
                <div class="category-checkbox-grid">
                  <label *ngFor="let cat of categories" class="cat-checkbox-chip" [class.selected]="isCategorySelected(cat.slug)">
                    <input
                      type="checkbox"
                      [checked]="isCategorySelected(cat.slug)"
                      (change)="toggleCategorySelection(cat.slug)"
                    />
                    <span class="cat-chip-icon">{{ cat.icon || '🏷️' }}</span>
                    <span class="cat-chip-name">{{ cat.name }}</span>
                  </label>
                </div>
                <p class="sub-text" style="font-size:11px; margin:5px 0 0 0;">
                  Links collection across multiple parent categories (e.g., Phone Cases, Desk Mats, Posters).
                </p>
              </div>

              <!-- PARENT SUB-CATEGORY SELECT (CASCADED) -->
              <div class="form-group">
                <label class="form-label">Parent Sub-Category</label>
                <select class="form-control" [(ngModel)]="currentCollection.subCategorySlug" name="colSubCategory" (ngModelChange)="onCollectionSubCategoryChange($event)">
                  <option value="">General / All Sub-Categories</option>
                  <option *ngFor="let sub of getSubCategoriesForCategory(currentCollection.category)" [value]="sub.slug">
                    {{ sub.icon }} {{ sub.name }}
                  </option>
                </select>
                <p class="sub-text" style="font-size:11px; margin:4px 0 0 0;">
                  Links collection to specific subcategory under the selected category.
                </p>
              </div>

              <div class="form-group">
                <label class="form-label">Collection Name *</label>
                <input type="text" class="form-control" [(ngModel)]="currentCollection.name" name="colName" placeholder="e.g. Porsche Series, Lumina Glitter" required />
              </div>

              <div class="form-group">
                <label class="form-label">Icon Emoji</label>
                <input type="text" class="form-control" [(ngModel)]="currentCollection.icon" name="colIcon" placeholder="🏎️, ✨, 🐆, 🖤" />
              </div>

              <div class="form-group">
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="2" [(ngModel)]="currentCollection.description" name="colDesc" placeholder="Short collection description..."></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Cover Image</label>
                <app-image-uploader
                  [currentImageUrl]="currentCollection.imageUrl || ''"
                  [cloudinaryId]="currentCollection.cloudinaryId || ''"
                  (imageUploaded)="onCollectionImageUploaded($event)"
                  (imageRemoved)="onCollectionImageRemoved()"
                ></app-image-uploader>
              </div>

              <div class="form-actions">
                <button *ngIf="editingCollectionId" type="button" (click)="resetCollectionForm()" class="btn-secondary">Cancel</button>
                <button type="submit" class="btn-gold" [disabled]="savingCollection">
                  <span class="spinner" *ngIf="savingCollection"></span>
                  <svg *ngIf="!savingCollection" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17 21 17 13 7 13 7 21"/>
                  </svg>
                  {{ savingCollection ? 'Saving…' : (editingCollectionId ? 'Update Collection' : 'Create Collection') }}
                </button>
              </div>
            </form>
          </section>

          <!-- COLLECTIONS TABLE -->
          <section class="glass-card list-card">
            <!-- Filter Toolbar -->
            <div class="subcat-toolbar">
              <div class="filter-group">
                <label class="form-label" style="margin:0; font-size:12px;">Category:</label>
                <select class="form-control" style="width: auto; min-width: 150px;" [(ngModel)]="filterCollectionCategory">
                  <option value="all">All Categories</option>
                  <option *ngFor="let cat of categories" [value]="cat.slug">
                    {{ cat.icon }} {{ cat.name }}
                  </option>
                </select>
              </div>

              <div class="filter-group">
                <label class="form-label" style="margin:0; font-size:12px;">Sub-Category:</label>
                <select class="form-control" style="width: auto; min-width: 150px;" [(ngModel)]="filterCollectionSubCategory">
                  <option value="all">All Sub-Categories</option>
                  <option *ngFor="let sub of getSubCategoriesForCategory(filterCollectionCategory)" [value]="sub.slug">
                    {{ sub.icon }} {{ sub.name }}
                  </option>
                </select>
              </div>

              <div class="search-wrap">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  class="form-control search-input"
                  [(ngModel)]="searchCollectionQuery"
                  placeholder="Search collections..."
                />
              </div>
            </div>

            <div class="table-wrap">
              <table class="custom-table">
                <thead>
                  <tr>
                    <th style="width: 44px;">#</th>
                    <th>Collection</th>
                    <th>Category Hierarchy</th>
                    <th style="text-align: right;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let col of getFilteredCollections(); let i = index">
                    <td class="sub-text">{{ i + 1 }}</td>
                    <td>
                      <div style="display:flex; align-items:center; gap:12px;">
                        <img [src]="col.imageUrl || '/images/collections/luxury_gold.jpg'" [alt]="col.name" style="width:40px; height:40px; border-radius:8px; object-fit:cover; border:1px solid var(--border-color);" />
                        <div>
                          <div style="display:flex; align-items:center; gap:6px;">
                            <span style="font-size:15px;">{{ col.icon || '✨' }}</span>
                            <strong style="color:var(--text-primary); font-size:13.5px;">{{ col.name }}</strong>
                          </div>
                          <div class="sub-text" style="font-size:11px;">{{ col.slug }}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style="display:flex; flex-direction:column; gap:4px;">
                        <div style="display:flex; flex-wrap:wrap; gap:4px;">
                          <span *ngFor="let catSlug of getCollectionCategories(col)" class="badge badge-gold" style="width:fit-content; font-size:11px; padding:2px 8px;">
                            {{ getCategoryDisplayName(catSlug) }}
                          </span>
                        </div>
                        <span *ngIf="col.subCategoryName || col.subCategorySlug" class="sub-text" style="font-size:11px;">
                          ↳ {{ col.subCategoryName || col.subCategorySlug }}
                        </span>
                      </div>
                    </td>
                    <td style="text-align: right;">
                      <div class="action-btn-group">
                        <button (click)="editCollection(col)" class="btn-secondary btn-icon" title="Edit Collection">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button (click)="deleteCollection(col)" class="btn-danger btn-icon" title="Delete Collection">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr *ngIf="getFilteredCollections().length === 0">
                    <td colspan="4" class="text-center" style="padding: 36px;">
                      <div style="font-size:26px; margin-bottom:6px;">✨</div>
                      <div style="color:var(--text-secondary);">No collections match this filter. Create one using the form on the left!</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

      </main>
    </div>
  `,
  styles: [`
    .tabs-bar {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px;
      margin-bottom: 24px;
      border-radius: var(--radius-md, 12px);
      width: fit-content;
      background: var(--bg-card);
      border: 1px solid var(--border-color);
      box-shadow: var(--shadow-sm);
    }

    .tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      border-radius: var(--radius-sm, 8px);
      border: 1px solid transparent;
      background: transparent;
      color: var(--text-secondary);
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
      transition: all var(--transition-fast, 0.2s);
    }

    .tab-btn:hover {
      color: var(--text-primary);
      background: var(--bg-hover);
    }

    .tab-btn.active {
      color: var(--accent-gold);
      background: var(--accent-gold-glow);
      border-color: var(--border-highlight);
      font-weight: 700;
    }

    .tab-count {
      font-size: 11px;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: var(--radius-full, 999px);
      background: rgba(125, 125, 125, 0.15);
      color: var(--text-secondary);
      transition: all 0.2s;
    }

    .tab-btn:hover .tab-count {
      color: var(--text-primary);
    }

    .tab-btn.active .tab-count {
      background: var(--accent-gold);
      color: #0B0F17;
    }

    /* Light Theme explicit high-contrast rules */
    :host-context(.light-theme) .tabs-bar {
      background: #FFFFFF;
      border: 1px solid rgba(0, 0, 0, 0.10);
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    }

    :host-context(.light-theme) .tab-btn {
      color: #334155;
    }

    :host-context(.light-theme) .tab-btn:hover {
      color: #0F172A;
      background: rgba(0, 0, 0, 0.05);
    }

    :host-context(.light-theme) .tab-btn.active {
      color: #8C6D23;
      background: rgba(201, 169, 110, 0.16);
      border-color: rgba(184, 146, 79, 0.45);
    }

    :host-context(.light-theme) .tab-count {
      background: #E2E8F0;
      color: #475569;
    }

    :host-context(.light-theme) .tab-btn.active .tab-count {
      background: #B8924F;
      color: #FFFFFF;
    }

    .category-layout {
      display: grid;
      grid-template-columns: 360px 1fr;
      gap: 24px;
    }

    @media (max-width: 960px) {
      .category-layout { grid-template-columns: 1fr; }
    }

    .add-card {
      padding: 24px;
      height: fit-content;
      border: 1px solid var(--border-color);
      background: var(--bg-card);
    }

    .form-card-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--border-color);
    }

    .add-cat-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .mobile-toggle-card {
      background: rgba(212, 175, 55, 0.04);
      border: 1px solid rgba(212, 175, 55, 0.2);
      border-radius: 8px;
      padding: 10px 12px;
    }

    .checkbox-label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      user-select: none;
      font-size: 13px;
    }

    .checkbox-label input[type="checkbox"] {
      width: 16px;
      height: 16px;
      accent-color: var(--accent-gold);
      cursor: pointer;
    }

    .form-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 8px;
    }

    .list-card {
      padding: 24px;
      border: 1px solid var(--border-color);
      background: var(--bg-card);
    }

    .list-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--border-color);
    }

    .count-pill {
      font-size: 12px;
      font-weight: 700;
      padding: 4px 12px;
      border-radius: 20px;
      background: rgba(212, 175, 55, 0.1);
      color: var(--accent-gold);
      border: 1px solid rgba(212, 175, 55, 0.25);
    }

    .nav-chip-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 5px 10px;
      border-radius: var(--radius-sm, 8px);
      font-size: 12px;
      font-weight: 600;
      color: var(--accent-gold);
      background: rgba(201, 169, 110, 0.08);
      border: 1px solid rgba(201, 169, 110, 0.25);
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .nav-chip-btn:hover {
      background: rgba(201, 169, 110, 0.18);
      border-color: var(--accent-gold);
      transform: translateY(-1px);
    }

    /* Sub-Categories Toolbar */
    .subcat-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      margin-bottom: 20px;
      flex-wrap: wrap;
    }

    .filter-group {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .search-wrap {
      position: relative;
      min-width: 220px;
    }

    .search-wrap svg {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-muted);
      pointer-events: none;
    }

    .search-input {
      padding-left: 32px;
      font-size: 12.5px;
    }

    /* Tables */
    .table-wrap {
      overflow-x: auto;
    }

    .custom-table {
      width: 100%;
      border-collapse: collapse;
      text-align: left;
    }

    .custom-table th {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--text-muted);
      padding: 10px 14px;
      border-bottom: 1px solid var(--border-color);
      font-weight: 700;
    }

    .custom-table td {
      padding: 13px 14px;
      border-bottom: 1px solid var(--border-color);
      font-size: 13px;
      vertical-align: middle;
    }

    .custom-table tbody tr {
      transition: background 0.15s ease;
    }

    .custom-table tbody tr:hover {
      background: var(--bg-hover);
    }

    .badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      font-weight: 700;
      padding: 3px 8px;
      border-radius: var(--radius-sm, 6px);
    }

    .badge-gold {
      background: rgba(201, 169, 110, 0.12);
      color: var(--accent-gold);
      border: 1px solid rgba(201, 169, 110, 0.3);
    }

    .badge-secondary {
      background: var(--bg-hover);
      color: var(--text-secondary);
      border: 1px solid var(--border-color);
    }

    .status-pill {
      font-size: 11px;
      font-weight: 700;
      padding: 3px 8px;
      border-radius: 12px;
      display: inline-block;
    }

    .status-active {
      background: rgba(34, 197, 94, 0.12);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.25);
    }

    .status-inactive {
      background: rgba(125, 125, 125, 0.1);
      color: #9ca3af;
      border: 1px solid var(--border-color);
    }

    .action-btn-group {
      display: inline-flex;
      gap: 6px;
    }

    .btn-icon {
      padding: 6px;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: var(--text-secondary);
    }

    .category-checkbox-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 6px;
    }

    .cat-checkbox-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: var(--bg-hover, rgba(255,255,255,0.05));
      border: 1px solid var(--border-color, rgba(255,255,255,0.1));
      border-radius: 8px;
      cursor: pointer;
      font-size: 13px;
      transition: all 0.2s ease;
      user-select: none;
    }

    .cat-checkbox-chip:hover {
      border-color: var(--accent-gold, #c9a96e);
      background: rgba(201, 169, 110, 0.08);
    }

    .cat-checkbox-chip.selected {
      background: rgba(201, 169, 110, 0.16);
      border-color: var(--accent-gold, #c9a96e);
      color: var(--accent-gold, #c9a96e);
      font-weight: 600;
    }

    .cat-checkbox-chip input[type="checkbox"] {
      cursor: pointer;
      accent-color: var(--accent-gold, #c9a96e);
    }
  `]
})
export class CategoryListComponent implements OnInit {
  activeTab: 'categories' | 'collections' | 'subcategories' = 'categories';

  categories: Category[] = [];
  collections: Collection[] = [];
  subCategories: SubCategory[] = [];

  // Category form state
  savingCat = false;
  editingCatId: string | null = null;
  currentCat: Partial<Category> = {
    name: '',
    icon: '📱',
    description: '',
    imageUrl: '',
    cloudinaryId: '',
    isMobile: true,
    showInShopMenu: true
  };

  // Collection form state
  savingCollection = false;
  editingCollectionId: string | null = null;
  currentCollection: Partial<Collection> = {
    name: '',
    category: 'mobile-cases',
    categoryName: 'Mobile Cases',
    categories: ['mobile-cases'],
    categoryNames: ['Mobile Cases'],
    icon: '✨',
    description: '',
    imageUrl: '',
    cloudinaryId: '',
    isMobile: true
  };

  // SubCategory form state
  savingSubCat = false;
  editingSubCatId: string | null = null;
  currentSubCat: Partial<SubCategory> = {
    name: '',
    category: '',
    icon: '🏷️',
    description: '',
    active: true,
    imageUrl: '',
    cloudinaryId: ''
  };

  // Filters & searches
  filterCollectionCategory = 'all';
  filterCollectionSubCategory = 'all';
  filterSubCategoryCategory = 'all';
  searchCategoryQuery = '';
  searchCollectionQuery = '';
  searchSubCatQuery = '';

  constructor(
    private categoryService: CategoryService,
    private collectionService: CollectionService,
    private subCategoryService: SubCategoryService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['tab'] === 'collections' || window.location.pathname.includes('/collections')) {
        this.activeTab = 'collections';
      } else if (params['tab'] === 'subcategories' || window.location.pathname.includes('/subcategories')) {
        this.activeTab = 'subcategories';
      } else if (params['tab'] === 'categories' || window.location.pathname.includes('/categories')) {
        this.activeTab = 'categories';
      }
      if (params['category']) {
        this.filterSubCategoryCategory = params['category'];
        this.filterCollectionCategory = params['category'];
      }
      if (params['subcategory']) {
        this.filterCollectionSubCategory = params['subcategory'];
      }
    });

    if (window.location.pathname.includes('/collections')) {
      this.activeTab = 'collections';
    } else if (window.location.pathname.includes('/subcategories')) {
      this.activeTab = 'subcategories';
    }

    this.loadCategories();
    this.loadCollections();
    this.loadSubCategories();
  }

  setTab(tab: 'categories' | 'collections' | 'subcategories'): void {
    this.activeTab = tab;
  }

  loadCategories(): void {
    this.categoryService.getCategories().subscribe({
      next: (res) => {
        this.categories = res;
        if (res.length > 0) {
          if (!this.currentCollection.category) {
            this.currentCollection.category = res[0].slug;
            this.currentCollection.categoryId = res[0]._id;
            this.currentCollection.categoryName = res[0].name;
          }
          if (!this.currentSubCat.category) {
            this.currentSubCat.category = res[0].slug;
            this.currentSubCat.categoryId = res[0]._id;
            this.currentSubCat.categoryName = res[0].name;
          }
        }
      },
      error: (err) => console.error(err)
    });
  }

  loadCollections(): void {
    this.collectionService.getCollections().subscribe({
      next: (res) => {
        this.collections = res;
      },
      error: (err) => console.error(err)
    });
  }

  loadSubCategories(): void {
    this.subCategoryService.getSubCategories().subscribe({
      next: (res) => (this.subCategories = res),
      error: (err) => console.error(err)
    });
  }

  // ─── Category CRUD ─────────────────────────────
  onCatImageUploaded(data: { url: string; cloudinaryId: string }): void {
    this.currentCat.imageUrl = data.url;
    this.currentCat.cloudinaryId = data.cloudinaryId;
  }

  onCatImageRemoved(): void {
    this.currentCat.imageUrl = '';
    this.currentCat.cloudinaryId = '';
  }

  editCategory(cat: Category): void {
    this.editingCatId = cat._id || null;
    this.currentCat = {
      ...cat,
      isMobile: cat.isMobile !== false,
      showInShopMenu: cat.showInShopMenu !== false
    };
  }

  resetCatForm(): void {
    this.editingCatId = null;
    this.currentCat = {
      name: '',
      icon: '📱',
      description: '',
      imageUrl: '',
      cloudinaryId: '',
      isMobile: true,
      showInShopMenu: true
    };
  }

  toggleShopMenuVisibility(cat: Category): void {
    if (!cat._id) return;
    const newVal = cat.showInShopMenu === false ? true : false;
    this.categoryService.updateCategory(cat._id, { showInShopMenu: newVal }).subscribe({
      next: (updated) => {
        cat.showInShopMenu = updated.showInShopMenu;
      },
      error: (err) => alert('Failed to update shop menu visibility: ' + err.message)
    });
  }

  onSaveCategory(): void {
    if (!this.currentCat.name?.trim()) return;
    this.savingCat = true;

    if (this.editingCatId) {
      this.categoryService.updateCategory(this.editingCatId, this.currentCat).subscribe({
        next: (updated) => {
          this.savingCat = false;
          const idx = this.categories.findIndex((c) => c._id === this.editingCatId);
          if (idx !== -1) this.categories[idx] = updated;
          this.resetCatForm();
        },
        error: (err) => {
          this.savingCat = false;
          alert('Failed to update category: ' + err.message);
        }
      });
    } else {
      this.categoryService.createCategory(this.currentCat).subscribe({
        next: (cat) => {
          this.savingCat = false;
          this.categories.push(cat);
          this.resetCatForm();
        },
        error: (err) => {
          this.savingCat = false;
          alert('Failed to create category: ' + (err.error?.message || err.message));
        }
      });
    }
  }

  deleteCategory(cat: Category): void {
    if (!cat._id) return;
    if (confirm(`Delete category "${cat.name}"? Sub-categories and collections linked to this category should be reassigned.`)) {
      this.categoryService.deleteCategory(cat._id).subscribe({
        next: () => {
          this.categories = this.categories.filter((c) => c._id !== cat._id);
          if (this.editingCatId === cat._id) this.resetCatForm();
        },
        error: (err) => alert('Failed to delete category: ' + err.message)
      });
    }
  }

  // ─── Collection CRUD ─────────────────────────────
  onCollectionCategoryChange(catSlug: string): void {
    const found = this.categories.find(c => c.slug === catSlug);
    if (found) {
      this.currentCollection.categoryId = found._id;
      this.currentCollection.categoryName = found.name;
    }
    this.currentCollection.subCategorySlug = '';
    this.currentCollection.subCategoryId = undefined;
    this.currentCollection.subCategoryName = '';
  }

  onCollectionSubCategoryChange(subSlug: string): void {
    if (!subSlug) {
      this.currentCollection.subCategorySlug = '';
      this.currentCollection.subCategoryId = undefined;
      this.currentCollection.subCategoryName = '';
      return;
    }
    const found = this.subCategories.find(s => s.slug === subSlug);
    if (found) {
      this.currentCollection.subCategoryId = found._id;
      this.currentCollection.subCategorySlug = found.slug;
      this.currentCollection.subCategoryName = found.name;
      if (found.category) {
        this.currentCollection.category = found.category;
        const parentCat = this.categories.find(c => c.slug === found.category);
        if (parentCat) {
          this.currentCollection.categoryId = parentCat._id;
          this.currentCollection.categoryName = parentCat.name;
        }
      }
    }
  }

  onCollectionImageUploaded(data: { url: string; cloudinaryId: string }): void {
    this.currentCollection.imageUrl = data.url;
    this.currentCollection.cloudinaryId = data.cloudinaryId;
  }

  onCollectionImageRemoved(): void {
    this.currentCollection.imageUrl = '';
    this.currentCollection.cloudinaryId = '';
  }

  getCollectionCategories(col: Collection): string[] {
    if (col.categories && col.categories.length > 0) {
      return col.categories;
    }
    if (col.category) {
      return [col.category];
    }
    return ['mobile-cases'];
  }

  isCategorySelected(slug: string): boolean {
    const list = this.currentCollection.categories || (this.currentCollection.category ? [this.currentCollection.category] : []);
    return list.includes(slug);
  }

  toggleCategorySelection(slug: string): void {
    let list = [...(this.currentCollection.categories || (this.currentCollection.category ? [this.currentCollection.category] : []))];
    const idx = list.indexOf(slug);
    if (idx > -1) {
      if (list.length > 1) {
        list.splice(idx, 1);
      }
    } else {
      list.push(slug);
    }
    this.currentCollection.categories = list;
    this.currentCollection.category = list[0] || slug;

    // sync categoryIds and categoryNames
    const matchedCats = this.categories.filter(c => list.includes(c.slug));
    this.currentCollection.categoryIds = matchedCats.map(c => c._id!).filter(Boolean);
    this.currentCollection.categoryNames = matchedCats.map(c => c.name);
    if (matchedCats.length > 0) {
      this.currentCollection.categoryId = matchedCats[0]._id;
      this.currentCollection.categoryName = matchedCats[0].name;
    }
  }

  editCollection(col: Collection): void {
    this.editingCollectionId = col._id || null;
    const cats = (col.categories && col.categories.length > 0) ? col.categories : (col.category ? [col.category] : ['mobile-cases']);
    const matchedCats = this.categories.filter(c => cats.includes(c.slug));
    this.currentCollection = {
      ...col,
      category: cats[0] || 'mobile-cases',
      categoryId: matchedCats[0]?._id || col.categoryId,
      categoryName: matchedCats[0]?.name || col.categoryName,
      categories: cats,
      categoryIds: matchedCats.length > 0 ? matchedCats.map(c => c._id!).filter(Boolean) : (col.categoryIds || []),
      categoryNames: matchedCats.length > 0 ? matchedCats.map(c => c.name) : (col.categoryNames || []),
      subCategorySlug: col.subCategorySlug || '',
      isMobile: col.isMobile !== false
    };
  }

  resetCollectionForm(): void {
    this.editingCollectionId = null;
    const defaultCat = this.categories[0]?.slug || 'mobile-cases';
    const defaultCatName = this.categories[0]?.name || 'Mobile Cases';
    const defaultCatId = this.categories[0]?._id;
    this.currentCollection = {
      name: '',
      category: defaultCat,
      categoryId: defaultCatId,
      categoryName: defaultCatName,
      categories: [defaultCat],
      categoryIds: defaultCatId ? [defaultCatId] : [],
      categoryNames: [defaultCatName],
      subCategorySlug: '',
      subCategoryName: '',
      icon: '✨',
      description: '',
      imageUrl: '',
      cloudinaryId: '',
      isMobile: true
    };
  }

  onSaveCollection(): void {
    if (!this.currentCollection.name?.trim()) return;
    this.savingCollection = true;

    // Ensure categories array has selected categories
    if (!this.currentCollection.categories || this.currentCollection.categories.length === 0) {
      this.currentCollection.categories = [this.currentCollection.category || 'mobile-cases'];
    }
    const matchedCats = this.categories.filter(c => this.currentCollection.categories?.includes(c.slug));
    this.currentCollection.categoryIds = matchedCats.map(c => c._id!).filter(Boolean);
    this.currentCollection.categoryNames = matchedCats.map(c => c.name);
    if (matchedCats.length > 0) {
      this.currentCollection.category = matchedCats[0].slug;
      this.currentCollection.categoryId = matchedCats[0]._id;
      this.currentCollection.categoryName = matchedCats[0].name;
    }

    // Sync SubCategory ObjectId & names
    if (this.currentCollection.subCategorySlug) {
      const sub = this.subCategories.find(s => s.slug === this.currentCollection.subCategorySlug || s._id === this.currentCollection.subCategoryId);
      if (sub) {
        this.currentCollection.subCategoryId = sub._id;
        this.currentCollection.subCategorySlug = sub.slug;
        this.currentCollection.subCategoryName = sub.name;
      }
    }

    if (this.editingCollectionId) {
      this.collectionService.updateCollection(this.editingCollectionId, this.currentCollection).subscribe({
        next: (updated) => {
          this.savingCollection = false;
          const idx = this.collections.findIndex((c) => c._id === this.editingCollectionId);
          if (idx !== -1) this.collections[idx] = updated;
          this.resetCollectionForm();
        },
        error: (err) => {
          this.savingCollection = false;
          alert('Failed to update collection: ' + err.message);
        }
      });
    } else {
      this.collectionService.createCollection(this.currentCollection).subscribe({
        next: (col) => {
          this.savingCollection = false;
          this.collections.push(col);
          this.resetCollectionForm();
        },
        error: (err) => {
          this.savingCollection = false;
          alert('Failed to create collection: ' + (err.error?.message || err.message));
        }
      });
    }
  }

  deleteCollection(col: Collection): void {
    if (!col._id) return;
    if (confirm(`Delete collection "${col.name}"? Products linked to this collection should be updated.`)) {
      this.collectionService.deleteCollection(col._id).subscribe({
        next: () => {
          this.collections = this.collections.filter((c) => c._id !== col._id);
          if (this.editingCollectionId === col._id) this.resetCollectionForm();
        },
        error: (err) => alert('Failed to delete collection: ' + err.message)
      });
    }
  }

  // ─── SubCategory CRUD ────────────────────────────
  onSubCategoryParentChange(catSlug: string): void {
    const found = this.categories.find(c => c.slug === catSlug);
    if (found) {
      this.currentSubCat.category = found.slug;
      this.currentSubCat.categoryId = found._id;
      this.currentSubCat.categoryName = found.name;
    }
  }

  onSubCatImageUploaded(data: { url: string; cloudinaryId: string }): void {
    this.currentSubCat.imageUrl = data.url;
    this.currentSubCat.cloudinaryId = data.cloudinaryId;
  }

  onSubCatImageRemoved(): void {
    this.currentSubCat.imageUrl = '';
    this.currentSubCat.cloudinaryId = '';
  }

  editSubCategory(sub: SubCategory): void {
    this.editingSubCatId = sub._id || null;
    this.currentSubCat = {
      ...sub,
      category: sub.category || sub.collectionSlug
    };
  }

  resetSubCatForm(): void {
    this.editingSubCatId = null;
    this.currentSubCat = {
      name: '',
      category: this.categories[0]?.slug || 'mobile-cases',
      categoryId: this.categories[0]?._id,
      categoryName: this.categories[0]?.name || 'Mobile Cases',
      icon: '🏷️',
      description: '',
      active: true,
      imageUrl: '',
      cloudinaryId: ''
    };
  }

  onSaveSubCategory(): void {
    if (!this.currentSubCat.name?.trim() || !this.currentSubCat.category) return;
    this.savingSubCat = true;

    // ensure categoryId and categoryName are synced
    const parentCat = this.categories.find(c => c.slug === this.currentSubCat.category || c._id === this.currentSubCat.categoryId);
    if (parentCat) {
      this.currentSubCat.categoryId = parentCat._id;
      this.currentSubCat.category = parentCat.slug;
      this.currentSubCat.categoryName = parentCat.name;
    }

    if (this.editingSubCatId) {
      this.subCategoryService.updateSubCategory(this.editingSubCatId, this.currentSubCat).subscribe({
        next: (updated) => {
          this.savingSubCat = false;
          const idx = this.subCategories.findIndex((s) => s._id === this.editingSubCatId);
          if (idx !== -1) this.subCategories[idx] = updated;
          this.resetSubCatForm();
        },
        error: (err) => {
          this.savingSubCat = false;
          alert('Failed to update subcategory: ' + err.message);
        }
      });
    } else {
      this.subCategoryService.createSubCategory(this.currentSubCat).subscribe({
        next: (created) => {
          this.savingSubCat = false;
          this.subCategories.push(created);
          this.resetSubCatForm();
        },
        error: (err) => {
          this.savingSubCat = false;
          alert('Failed to create subcategory: ' + (err.error?.message || err.message));
        }
      });
    }
  }

  deleteSubCategory(sub: SubCategory): void {
    if (!sub._id) return;
    if (confirm(`Delete sub-category "${sub.name}"?`)) {
      this.subCategoryService.deleteSubCategory(sub._id).subscribe({
        next: () => {
          this.subCategories = this.subCategories.filter((s) => s._id !== sub._id);
          if (this.editingSubCatId === sub._id) this.resetSubCatForm();
        },
        error: (err) => alert('Failed to delete subcategory: ' + err.message)
      });
    }
  }

  // ─── Hierarchy & Filter Helpers ─────────────────
  getSubCategoriesForCategory(catSlug?: string): SubCategory[] {
    if (!catSlug || catSlug === 'all') return this.subCategories;
    return this.subCategories.filter(s =>
      (s.category && s.category.toLowerCase() === catSlug.toLowerCase()) ||
      (s.categoryId && this.categories.find(c => c._id === s.categoryId)?.slug === catSlug)
    );
  }

  getSubCategoryCountForCategory(catSlug: string): number {
    return this.getSubCategoriesForCategory(catSlug).length;
  }

  getCollectionCountForCategory(catSlug: string): number {
    return this.collections.filter(c => this.getCollectionCategories(c).includes(catSlug) || (c.category || 'mobile-cases') === catSlug).length;
  }

  getCollectionCountForSubCategory(subSlug: string): number {
    if (!subSlug) return 0;
    return this.collections.filter(c =>
      c.subCategorySlug === subSlug ||
      (c.subCategoryId && this.subCategories.find(s => s._id === c.subCategoryId)?.slug === subSlug)
    ).length;
  }

  openSubCategoriesForCategory(catSlug: string): void {
    this.filterSubCategoryCategory = catSlug;
    this.activeTab = 'subcategories';
  }

  openCollectionsForSubCategory(subSlug: string): void {
    const sub = this.subCategories.find(s => s.slug === subSlug);
    if (sub && sub.category) {
      this.filterCollectionCategory = sub.category;
    }
    this.filterCollectionSubCategory = subSlug;
    this.activeTab = 'collections';
  }

  getCategoryDisplayName(catSlug: string): string {
    const found = this.categories.find(c => c.slug === catSlug || c._id === catSlug);
    return found ? found.name : (catSlug === 'mobile-cases' ? 'Mobile Cases' : (catSlug || 'General'));
  }

  getCollectionDisplayName(colSlug: string): string {
    const found = this.collections.find(c => c.slug === colSlug || c._id === colSlug);
    return found ? found.name : colSlug;
  }

  getFilteredCategories(): Category[] {
    if (!this.searchCategoryQuery.trim()) return this.categories;
    const q = this.searchCategoryQuery.toLowerCase().trim();
    return this.categories.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.slug.toLowerCase().includes(q) ||
      (c.description && c.description.toLowerCase().includes(q))
    );
  }

  getFilteredCollections(): Collection[] {
    let list = this.collections;
    if (this.filterCollectionCategory !== 'all') {
      list = list.filter(c => {
        const cats = this.getCollectionCategories(c);
        return cats.includes(this.filterCollectionCategory) || (c.category || 'mobile-cases') === this.filterCollectionCategory;
      });
    }
    if (this.filterCollectionSubCategory !== 'all') {
      list = list.filter(c => (c.subCategorySlug || c.subCategoryName) === this.filterCollectionSubCategory || c.subCategoryId === this.filterCollectionSubCategory);
    }
    if (this.searchCollectionQuery.trim()) {
      const q = this.searchCollectionQuery.toLowerCase().trim();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        (c.categoryName && c.categoryName.toLowerCase().includes(q)) ||
        (c.subCategoryName && c.subCategoryName.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q))
      );
    }
    return list;
  }

  getFilteredSubCategories(): SubCategory[] {
    let list = this.subCategories;
    if (this.filterSubCategoryCategory !== 'all') {
      list = list.filter(s =>
        (s.category && s.category.toLowerCase() === this.filterSubCategoryCategory.toLowerCase()) ||
        s.categoryId === this.filterSubCategoryCategory
      );
    }
    if (this.searchSubCatQuery.trim()) {
      const q = this.searchSubCatQuery.toLowerCase().trim();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.slug && s.slug.toLowerCase().includes(q)) ||
        (s.categoryName && s.categoryName.toLowerCase().includes(q)) ||
        (s.description && s.description.toLowerCase().includes(q))
      );
    }
    return list;
  }
}
